import React, { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext } from "@mui/lab";
import { Button, TextField } from "@mui/material";

import "./Section.css";
import placeholderImage from "../assets/placeholder-image.png";
import SuccessAlert from "../components/success-alert/SuccessAlert";
import ErrorAlert from "../components/error-alert/ErrorAlert";
import { toast } from "react-toastify";
import useLanguages from "../hooks/useLanguages";
import { API, SERVER_DOMAIN } from "../assets/staticData";
import ClearCacheButton from "../components/ClearCacheButton/ClearCacheButton";
import { CACHE_KEY } from "../constant/cache-key";

export default function General({ user }) {
  const langs = useLanguages();

  const [value, setValue] = useState("0");
  const [data, setData] = useState([]);
  const [returnedData, setReturnedData] = useState([]);
  const [logos, setLogos] = useState([]);
  const [favicons, setFavicons] = useState([]);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [loading, setLoading] = useState(true);

  const hiddenFileInput = useRef();
  const faveiconInput = useRef();
  let userToken = localStorage.getItem("userToken");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabStyle = { textTransform: "capitalize", fontWeight: "500" };

  const formData = new FormData();

  // Get Data from backend
  useEffect(() => {
    let docs = langs.map((item) => ({
      name: "",
      logo: null,
      favicon: null,
      language: item.attributes.language,
    }));
    setData(docs);
    let errorDocs = [];
    langs.map(() => {
      errorDocs.push({ error: false });
    });
    setErrors(errorDocs);
    let logosDocs = [];
    langs.map(() => {
      logosDocs.push({ logo: null });
    });
    setLogos(logosDocs);

    let faviconsDocs = [];
    langs.map(() => {
      faviconsDocs.push({ favicon: null });
    });
    setFavicons(faviconsDocs);

    async function getData() {
      await fetch(`${API}/general-settings?populate=*`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      }).then(async (res) => {
        const response = await res.json();
        const apiData = response.data?.attributes?.generalData || [];

        // Update state using the updater function to ensure we're working with the latest state
        setData((prevData) => {
          return prevData.map((localItem) => {
            const apiItem = apiData.find(
              (apiItem) => apiItem.language === localItem.language
            );

            if (apiItem) {
              return {
                ...localItem,
                name: apiItem.name || localItem.name,
                logo: apiItem.logo || localItem.logo,
                favicon: apiItem.favicon || localItem.favicon,
              };
            } else {
              return localItem;
            }
          });
        });
        setLoading(false);
      });
    }

    getData();
  }, [langs]);

  // Updates Website General settings
  const submitData = (item, index) => {
    if (
      data[index]?.name == "" &&
      logos[index].logo == null &&
      favicons[index].favicon == null
    ) {
      setErrors([
        ...errors.map((item, idx) => {
          if (idx === index) {
            return { ...item, error: true };
          } else {
            return item;
          }
        }),
      ]);
    } else if (logos[index]?.logo || favicons[index]?.favicon) {
      logos[index]?.logo &&
        formData.append("files", logos[index].logo, logos[index].logo?.name);

      favicons[index]?.favicon &&
        formData.append(
          "files",
          favicons[index].favicon,
          favicons[index].favicon?.name
        );

      fetch(`${API}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      }).then(async (res) => {
        const response = await res.json();
        let temp = [
          ...data.map((item, idx) => {
            if (idx === index) {
              return {
                ...item,
                logo: logos[index]?.logo
                  ? response[0]?.url ?? item.logo
                  : item.logo,
                favicon: logos[index]?.logo
                  ? response[1]?.url ?? item.favicon
                  : response[0]?.url ?? item.favicon,
              };
            } else {
              return item;
            }
          }),
        ];

        fetch(`${API}/general-settings`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({
            data: { generalData: temp },
          }),
        }).then(async (res) => {
          if (res.ok) {
            setSuccess(true);
          } else {
            setFail(true);
          }
        });
      });
    } else {
      fetch(`${API}/general-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          data: { generalData: data },
        }),
      }).then(async (res) => {
        if (res.ok) {
          setSuccess(true);
        } else {
          setFail(true);
        }
      });
    }
  };

  const handleLogoChange = (e, item, index) => {
    const file = e.target.files[0];

    if (file) {
      setLogos([
        ...logos.map((item, idx) => {
          if (idx === index) {
            return { ...item, logo: file };
          } else {
            return item;
          }
        }),
      ]);
    }
  };

  // handles Favicon Upload
  const handleFaviconChange = (e, item, index) => {
    const file = e.target.files[0];

    if (file) {
      setFavicons([
        ...favicons.map((item, idx) => {
          if (idx === index) {
            return { ...item, favicon: file };
          } else {
            return item;
          }
        }),
      ]);
    }
  };

  return (
    <TabContext value={value}>
      <Box sx={{ width: "100%" }}>
        <h3 style={{ textTransform: "capitalize" }}>General Settings</h3>

        <Box mt={2}>
          <ClearCacheButton
            keys={[
              CACHE_KEY.startupConfigs
            ]} />
        </Box>

        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs"
        >
          {langs?.map((item, index) => {
            return (
              <Tab
                key={index}
                sx={tabStyle}
                value={index.toString()}
                label={item.attributes.language}
                icon={
                  <img
                    src={
                      SERVER_DOMAIN + item.attributes.icon.data.attributes.url
                    }
                    style={{ width: 25, aspectRatio: 1, borderRadius: 20 }}
                  />
                }
                iconPosition="start"
              />
            );
          })}
        </Tabs>

        {loading ? (
          <></>
        ) : (
          <>
            {langs?.map((item, index) => {
              return (
                <TabPanel value={index.toString()} key={index}>
                  <SuccessAlert show={success} setShow={(v) => setSuccess(v)} />
                  <ErrorAlert show={fail} setShow={(v) => setFail(v)} />
                  <TextField
                    style={{
                      pointerEvents: user.perms[1].edit ? null : "none",
                    }}
                    sx={{ width: 400 }}
                    label="Website Name"
                    variant="outlined"
                    value={data[index]?.name || ""}
                    error={errors[index]?.error}
                    onChange={(e) => {
                      setData([
                        ...data.map((item, idx) => {
                          if (idx === index) {
                            return { ...item, name: e.target.value };
                          } else {
                            return item;
                          }
                        }),
                      ]);
                      setErrors([
                        ...errors.map((item, idx) => {
                          if (idx === index) {
                            return { ...item, error: false };
                          } else {
                            return item;
                          }
                        }),
                      ]);
                    }}
                  />
 
                  <div
                    style={{
                      display: "flex",
                      gap: 40,
                      marginTop: 20,
                      pointerEvents: user.perms[1].edit ? null : "none",
                    }}
                  >
                    <div>
                      <p
                        className="greyLabel"
                        style={{ color: errors[index]?.error ? "red" : null }}
                      >
                        Website Logo
                      </p>
                      <div
                        className="placeholderIMG"
                        onClick={() => hiddenFileInput.current.click()}
                      >
                        <img
                          style={{
                            objectFit: data[index]?.logo ? "contain" : "cover",
                          }}
                          src={
                            logos[index]?.logo
                              ? URL.createObjectURL(logos[index].logo)
                              : data[index]?.logo
                                ? SERVER_DOMAIN + data[index].logo
                                : placeholderImage
                          }
                        />
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoChange(e, item, index)}
                        ref={hiddenFileInput}
                        style={{ display: "none" }}
                      />
                    </div>

                    <div>
                      <p
                        className="greyLabel"
                        style={{ color: errors[index]?.error ? "red" : null }}
                      >
                        Website Favicon
                      </p>
                      <div
                        className="placeholderIMG"
                        onClick={() => faveiconInput.current.click()}
                        style={{
                          width: 200,
                          height: 200,
                          objectFit: data[index]?.favicon ? "contain" : "cover",
                        }}
                      >
                        <img
                          src={
                            favicons[index]?.favicon
                              ? URL.createObjectURL(favicons[index].favicon)
                              : data[index]?.favicon
                                ? SERVER_DOMAIN + data[index].favicon
                                : placeholderImage
                          }
                        />
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFaviconChange(e, item, index)}
                        ref={faveiconInput}
                        style={{ display: "none" }}
                      />
                    </div>
                  </div>

                  {user.perms[1].edit && (
                    <div
                      className="submitBTN"
                      onClick={() => submitData(item, index)}
                    >
                      Save Changes
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </>
        )}
      </Box>
    </TabContext>
  );
}
